import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import { CodeEditor, HolderInput, OutputDisplay } from '../components';
import { scriptTesterApi } from '../api/features/scriptTester/scriptTesterApi';
import { ScriptTesterPayload } from '../api/features/scriptTester/scriptTester.types';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`script-tester-tabpanel-${index}`}
      aria-labelledby={`script-tester-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: SAP_SPACING.md }}>{children}</Box>}
    </div>
  );
}

const ScriptTester: React.FC = () => {
  const { clientCode } = useClientCode();
  const [script, setScript] = useState('');
  const [holder, setHolder] = useState('');
  const [myCode, setMyCode] = useState('');
  const [output, setOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState(0);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleRun = async () => {
    if (!script.trim() || !holder.trim() || !myCode.trim()) {
      setError('Please provide script, holder, and myCode');
      return;
    }

    setIsLoading(true);
    setError(undefined);
    setOutput('');

    try {
      const payload: ScriptTesterPayload = {
        functionCode: script.trim(),
        holder: holder.trim(),
        myCode: myCode.trim()
      };

      const result = await scriptTesterApi.test({ 
        clientCode,
        payload
      });
      
      if (result.isSuccess) {
        setOutput(result.result);
      } else {
        setError(result.errorMessage || 'Script execution failed');
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Script execution failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setScript('');
    setHolder('');
    setMyCode('');
    setOutput('');
    setError(undefined);
  };

  const handleCopy = () => {
    // Copy to clipboard functionality
    navigator.clipboard.writeText(output);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, marginBottom: SAP_SPACING.xl }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SAP_SPACING.sm }}>
          <CodeIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h4" sx={{ color: SAP_COLORS.sapBlue, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
              Script Tester
            </Typography>
            <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5, fontSize: '0.9375rem' }}>
              Test your scripts with custom inputs and see the output
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleRun}
            disabled={isLoading || !script.trim() || !holder.trim() || !myCode.trim()}
            sx={{
              backgroundColor: SAP_COLORS.sapBlue,
              '&:hover': { backgroundColor: SAP_COLORS.sapBlueDark },
              px: 3,
              py: 1,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              minWidth: '100px',
            }}
          >
            {isLoading ? 'Testing...' : 'Run'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={isLoading}
            sx={{
              borderColor: SAP_COLORS.sapGray3,
              color: SAP_COLORS.sapGray6,
              '&:hover': { borderColor: SAP_COLORS.sapGray5, backgroundColor: SAP_COLORS.sapGray1 },
              px: 3,
              py: 1,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              minWidth: '100px',
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: SAP_COLORS.sapWhite, boxShadow: SAP_SHADOWS.card, border: `1px solid ${SAP_COLORS.sapGray2}`, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '600px' }}>
          {/* Left Column - Inputs with Tabs */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRight: { xs: 'none', md: `1px solid ${SAP_COLORS.sapGray2}` }
          }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: SAP_COLORS.sapGray2, bgcolor: SAP_COLORS.sapGray1 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{
                  '& .MuiTab-root': {
                    color: SAP_COLORS.sapGray6,
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                    textTransform: 'none',
                    minHeight: '48px',
                    '&.Mui-selected': {
                      color: SAP_COLORS.sapBlue,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: SAP_COLORS.sapBlue,
                  },
                }}
              >
                <Tab label="Script" id="script-tester-tab-0" aria-controls="script-tester-tabpanel-0" />
                <Tab label="Holder" id="script-tester-tab-1" aria-controls="script-tester-tabpanel-1" />
                <Tab label="My Code" id="script-tester-tab-2" aria-controls="script-tester-tabpanel-2" />
                {isMobile && <Tab label="Output" id="script-tester-tab-3" aria-controls="script-tester-tabpanel-3" />}
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ flex: 1, p: SAP_SPACING.lg }}>
              {/* Script Tab */}
              <TabPanel value={activeTab} index={0}>
                <CodeEditor
                  value={script}
                  onChange={setScript}
                  placeholder="Enter your script here...\nExample:\nfunction processData(data) {\n  return data.map(item => item.name);\n}"
                  label="Script"
                  disabled={isLoading}
                />
              </TabPanel>

              {/* Holder Tab */}
              <TabPanel value={activeTab} index={1}>
                <HolderInput
                  value={holder}
                  onChange={setHolder}
                  placeholder="Enter holder data...\nExample:\nname: John\ntitle: Developer"
                  label="Holder"
                  disabled={isLoading}
                />
              </TabPanel>

              {/* My Code Tab */}
              <TabPanel value={activeTab} index={2}>
                <CodeEditor
                  value={myCode}
                  onChange={setMyCode}
                  placeholder="Enter your custom code here...\nExample:\nconst result = processData(holder);\nconsole.log(result);"
                  label="My Code"
                  disabled={isLoading}
                />
              </TabPanel>

              {/* Output Tab (Mobile Only) */}
              {isMobile && (
                <TabPanel value={activeTab} index={3}>
                  <OutputDisplay
                    output={output}
                    isLoading={isLoading}
                    error={error}
                    onCopy={handleCopy}
                  />
                </TabPanel>
              )}
            </Box>
          </Box>

          {/* Right Column - Output (Desktop Only) */}
          {!isMobile && (
            <Box sx={{ flex: 1, p: SAP_SPACING.lg }}>
              <OutputDisplay
                output={output}
                isLoading={isLoading}
                error={error}
                onCopy={handleCopy}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ScriptTester;